import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSync,
  faSearch,
  faExclamationTriangle,
  faCheckCircle,
  faSpinner,
  faClock,
  faTrash,
  faRedo,
  faDatabase,
  faSliders,
  faChartBar,
  faCog,
  faFileAlt,
  faEye,
  faDollarSign,
  faFlask,
  faPlus,
  faPlay,
  faArrowLeft,
  faQuestionCircle,
  faListCheck,
  faTimes,
  faRobot,
  faChevronRight,
  faComment,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import { DocumentViewerModal } from '../components/DocumentViewerModal';
import { UsersTab } from '../components/admin/UsersTab';
import { useAuth } from '../contexts/AuthContext';
import type {
  RagDocument,
  RagDocumentStatus,
  RagDocumentChunk,
  RagSearchResult,
  RagStatsResponse,
  RagSyncResult,
  RagConfigResponse,
  RagCostsResponse,
  TestQuestion,
  TestSet,
  TestSetWithQuestions,
  TestRun,
  TestRunWithResults,
  TestRunConfig,
  QuestionResult,
} from '../types';

// ============================================================================
// Constants
// ============================================================================
const DARK_COLOR = '#444444';
const ACCENT_COLOR = '#fec35c';
const SUCCESS_COLOR = '#22c55e';
const WARNING_COLOR = '#f59e0b';
const ERROR_COLOR = '#ef4444';
const INFO_COLOR = '#3b82f6';
const PURPLE_COLOR = '#8b5cf6';
const STALE_HOURS = 6;

type TabId = 'dashboard' | 'documents' | 'chunks' | 'search' | 'costs' | 'errors' | 'tests' | 'feedback' | 'users' | 'settings';

// Tabs addressable as `/lupa-admin?tab=<id>` — Hub items link straight to them
// (hubs/hubs.ts). 'feedback' is its own page (/feedback-review), so not listed.
const URL_TABS: readonly TabId[] = ['dashboard', 'documents', 'chunks', 'search', 'costs', 'errors', 'tests', 'users', 'settings'];

// ============================================================================
// Helper Functions
// ============================================================================
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatCurrency = (amount: number): string => {
  if (amount < 0.01) {
    return `$${amount.toFixed(6)}`;
  }
  return `$${amount.toFixed(4)}`;
};

const formatTokens = (tokens: number): string => {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
};

const isStaleProcessing = (doc: RagDocument): boolean => {
  if (doc.status !== 'processing') return false;
  // Use updated_at (not created_at) - a retry updates updated_at but not created_at
  const updatedAt = new Date(doc.updated_at);
  const hoursDiff = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60);
  return hoursDiff > STALE_HOURS;
};

const getEffectiveStatus = (doc: RagDocument): RagDocumentStatus | 'stale' => {
  if (isStaleProcessing(doc)) return 'stale';
  return doc.status;
};

// ============================================================================
// Sub-components
// ============================================================================

const StatusBadge: React.FC<{ status: RagDocumentStatus | 'stale' }> = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'indexed':
        return { color: SUCCESS_COLOR, icon: faCheckCircle, label: 'Indexed' };
      case 'failed':
        return { color: ERROR_COLOR, icon: faExclamationTriangle, label: 'Failed' };
      case 'processing':
        return { color: ACCENT_COLOR, icon: faSpinner, label: 'Processing' };
      case 'pending':
        return { color: WARNING_COLOR, icon: faClock, label: 'Pending' };
      case 'stale':
        return { color: ERROR_COLOR, icon: faExclamationTriangle, label: 'Stale (>6h)' };
      default:
        return { color: DARK_COLOR, icon: faClock, label: status };
    }
  };

  const config = getStatusConfig();

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.25rem 0.625rem',
        borderRadius: '9999px',
        fontSize: '0.7rem',
        fontWeight: 500,
        backgroundColor: `${config.color}20`,
        color: config.color,
      }}
    >
      <FontAwesomeIcon
        icon={config.icon}
        style={{ width: 10, height: 10 }}
        spin={status === 'processing'}
      />
      {config.label}
    </span>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string | number;
  subtitle?: string;
  color?: string;
}> = ({ label, value, subtitle, color = DARK_COLOR }) => (
  <div
    style={{
      background: 'white',
      padding: '1.25rem',
      borderRadius: '4px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}
  >
    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.75rem', fontWeight: 700, color }}>
      {value}
    </div>
    {subtitle && (
      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
        {subtitle}
      </div>
    )}
  </div>
);

const SectionCard: React.FC<{
  title: string;
  icon?: typeof faDatabase;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, action, children }) => (
  <div
    style={{
      background: 'white',
      borderRadius: '4px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      marginBottom: '1.5rem',
    }}
  >
    <div
      style={{
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ fontSize: '1rem', fontWeight: 600, color: DARK_COLOR, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {icon && <FontAwesomeIcon icon={icon} style={{ color: ACCENT_COLOR }} />}
        {title}
      </div>
      {action}
    </div>
    <div style={{ padding: '1rem 1.5rem' }}>{children}</div>
  </div>
);

const ActionButton: React.FC<{
  onClick: () => void;
  icon?: typeof faSync;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  loading?: boolean;
}> = ({ onClick, icon, children, variant = 'secondary', disabled, loading }) => {
  const getStyles = () => {
    switch (variant) {
      case 'primary':
        return { background: ACCENT_COLOR, color: 'white', border: 'none' };
      case 'danger':
        return { background: 'transparent', color: ERROR_COLOR, border: `1px solid ${ERROR_COLOR}` };
      default:
        return { background: 'white', color: '#64748b', border: '1px solid #e2e8f0' };
    }
  };

  const styles = getStyles();

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        ...styles,
        padding: '0.5rem 1rem',
        borderRadius: '4px',
        fontSize: '0.8rem',
        fontWeight: 500,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        opacity: disabled || loading ? 0.6 : 1,
        transition: 'all 0.2s',
        fontFamily: 'Lato, system-ui, sans-serif',
      }}
    >
      {icon && <FontAwesomeIcon icon={loading ? faSpinner : icon} spin={loading} style={{ width: 12, height: 12 }} />}
      {children}
    </button>
  );
};

// ============================================================================
// Tab Components
// ============================================================================

interface DashboardTabProps {
  stats: RagStatsResponse | null;
  isLoading: boolean;
  onRefresh: () => void;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ stats, isLoading, onRefresh }) => {
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
        Loading dashboard...
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
        Failed to load stats.
        <ActionButton onClick={onRefresh} icon={faSync} variant="secondary">
          Retry
        </ActionButton>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <StatCard label="Total Documents" value={stats.total_documents} subtitle="Across all file types" />
        <StatCard label="Indexed" value={stats.indexed_documents} subtitle="Ready for search" color={SUCCESS_COLOR} />
        <StatCard label="Processing" value={stats.processing_documents} subtitle="In queue" color={WARNING_COLOR} />
        <StatCard label="Failed" value={stats.failed_documents} subtitle="Needs attention" color={ERROR_COLOR} />
        {stats.stale_processing > 0 && (
          <StatCard label="Stale" value={stats.stale_processing} subtitle={`Processing > ${STALE_HOURS}h`} color={ERROR_COLOR} />
        )}
        <StatCard label="Total Chunks" value={stats.total_chunks.toLocaleString()} subtitle="Indexed in Azure Search" color={INFO_COLOR} />
        <StatCard label="Storage Used" value={formatFileSize(stats.total_storage_bytes)} subtitle="In blob storage" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
        <SectionCard title="Document Health" icon={faChartBar}>
          {(() => {
            const total = stats.total_documents;
            if (total === 0) {
              return (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>
                  No documents yet. Click "Sync All Documents" to get started.
                </div>
              );
            }
            const indexedPct = (stats.indexed_documents / total) * 100;
            const processingPct = (stats.processing_documents / total) * 100;
            const failedPct = (stats.failed_documents / total) * 100;
            const stalePct = (stats.stale_processing / total) * 100;
            const pendingPct = (stats.pending_documents / total) * 100;

            return (
              <div>
                {/* Progress bar */}
                <div style={{
                  height: '12px',
                  borderRadius: '4px',
                  background: '#f1f5f9',
                  overflow: 'hidden',
                  display: 'flex',
                  marginBottom: '1rem',
                }}>
                  <div style={{ width: `${indexedPct}%`, background: SUCCESS_COLOR, transition: 'width 0.3s' }} />
                  <div style={{ width: `${processingPct}%`, background: WARNING_COLOR, transition: 'width 0.3s' }} />
                  <div style={{ width: `${stalePct}%`, background: ERROR_COLOR, transition: 'width 0.3s' }} />
                  <div style={{ width: `${failedPct}%`, background: '#ef4444', transition: 'width 0.3s' }} />
                  <div style={{ width: `${pendingPct}%`, background: '#94a3b8', transition: 'width 0.3s' }} />
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: SUCCESS_COLOR }} />
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Indexed ({stats.indexed_documents})</span>
                  </div>
                  {stats.processing_documents > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: WARNING_COLOR }} />
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Processing ({stats.processing_documents})</span>
                    </div>
                  )}
                  {stats.stale_processing > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: ERROR_COLOR }} />
                      <span style={{ fontSize: '0.8rem', color: ERROR_COLOR, fontWeight: 500 }}>Stale ({stats.stale_processing})</span>
                    </div>
                  )}
                  {stats.failed_documents > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444' }} />
                      <span style={{ fontSize: '0.8rem', color: ERROR_COLOR, fontWeight: 500 }}>Failed ({stats.failed_documents})</span>
                    </div>
                  )}
                  {stats.pending_documents > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#94a3b8' }} />
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Pending ({stats.pending_documents})</span>
                    </div>
                  )}
                </div>

                {/* Health message */}
                {(stats.failed_documents > 0 || stats.stale_processing > 0) && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: '#fef2f2',
                    borderRadius: '4px',
                    borderLeft: `3px solid ${ERROR_COLOR}`,
                    fontSize: '0.85rem',
                    color: '#991b1b',
                  }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                    {stats.failed_documents + stats.stale_processing} document(s) need attention. Check the Errors tab.
                  </div>
                )}
              </div>
            );
          })()}
        </SectionCard>

        <SectionCard title="Documents by Type" icon={faChartBar}>
          {Object.entries(stats.documents_by_type).length === 0 ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>
              No documents indexed
            </div>
          ) : (
            Object.entries(stats.documents_by_type).map(([type, count]) => (
              <div
                key={type}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{type.toUpperCase()}</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 500, color: DARK_COLOR }}>{count}</span>
              </div>
            ))
          )}
        </SectionCard>
      </div>
    </div>
  );
};

interface DocumentsTabProps {
  documents: RagDocument[];
  isLoading: boolean;
  onRefresh: () => void;
  onRetry: (id: string) => void;
  onDelete: (id: string) => void;
  onViewDocument: (doc: RagDocument) => void;
  retryingDocumentId?: string | null;
  retryStatus?: string | null;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  documents,
  isLoading,
  onRefresh,
  onRetry,
  onDelete,
  onViewDocument,
  retryingDocumentId,
  retryStatus,
}) => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredDocuments = documents.filter((doc) => {
    if (statusFilter !== 'all') {
      const effectiveStatus = getEffectiveStatus(doc);
      if (statusFilter === 'stale' && effectiveStatus !== 'stale') return false;
      if (statusFilter !== 'stale' && doc.status !== statusFilter) return false;
    }
    if (typeFilter !== 'all') {
      const ext = doc.filename.split('.').pop()?.toLowerCase() || '';
      if (ext !== typeFilter) return false;
    }
    if (searchQuery) {
      return doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  const fileTypes = [...new Set(documents.map((d) => d.filename.split('.').pop()?.toLowerCase() || ''))];

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '0.5rem 0.875rem',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            fontSize: '0.875rem',
            minWidth: '200px',
            fontFamily: 'Lato, system-ui, sans-serif',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '0.5rem 0.875rem',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            fontSize: '0.875rem',
            background: 'white',
            fontFamily: 'Lato, system-ui, sans-serif',
          }}
        >
          <option value="all">All Statuses</option>
          <option value="indexed">Indexed</option>
          <option value="processing">Processing</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
          <option value="stale">Stale (&gt;6h)</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{
            padding: '0.5rem 0.875rem',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            fontSize: '0.875rem',
            background: 'white',
            fontFamily: 'Lato, system-ui, sans-serif',
          }}
        >
          <option value="all">All Types</option>
          {fileTypes.map((type) => (
            <option key={type} value={type}>{type.toUpperCase()}</option>
          ))}
        </select>
        <ActionButton onClick={onRefresh} icon={faSync} loading={isLoading}>
          Refresh
        </ActionButton>
      </div>

      <SectionCard title={`Documents (${filteredDocuments.length})`} icon={faFileAlt}>
        {isLoading && documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
            Loading documents...
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            No documents found.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Filename</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Size</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Chunks</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Updated</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((doc) => {
                  const effectiveStatus = getEffectiveStatus(doc);
                  return (
                    <tr
                      key={doc.id}
                      style={{
                        background: effectiveStatus === 'failed' || effectiveStatus === 'stale' ? '#fef2f2' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ fontWeight: 500, color: DARK_COLOR }}>{doc.filename}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{doc.blob_path}</div>
                        {(effectiveStatus === 'failed' || effectiveStatus === 'stale') && doc.error_message && (
                          <div style={{ fontSize: '0.7rem', color: ERROR_COLOR, marginTop: '0.25rem' }}>
                            {doc.error_message}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>
                        {formatFileSize(doc.file_size_bytes)}
                      </td>
                      <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}>
                        <StatusBadge status={effectiveStatus} />
                      </td>
                      <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>
                        {doc.chunk_count || '-'}
                      </td>
                      <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>
                        {formatDate(doc.updated_at)}
                      </td>
                      <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => onViewDocument(doc)}
                            style={{
                              padding: '0.375rem 0.75rem',
                              border: '1px solid #e2e8f0',
                              background: 'white',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              color: '#64748b',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} style={{ width: 10, height: 10 }} />
                            View
                          </button>
                          <button
                            onClick={() => onRetry(doc.id)}
                            disabled={retryingDocumentId === doc.id}
                            style={{
                              padding: '0.375rem 0.75rem',
                              border: 'none',
                              background: retryingDocumentId === doc.id ? '#94a3b8' : WARNING_COLOR,
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: retryingDocumentId === doc.id ? 'wait' : 'pointer',
                              color: 'white',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              opacity: retryingDocumentId === doc.id ? 0.8 : 1,
                            }}
                          >
                            <FontAwesomeIcon
                              icon={retryingDocumentId === doc.id ? faSpinner : faRedo}
                              spin={retryingDocumentId === doc.id}
                              style={{ width: 10, height: 10 }}
                            />
                            {retryingDocumentId === doc.id ? (retryStatus || 'Re-indexing...') : 'Re-index'}
                          </button>
                          <button
                            onClick={() => onDelete(doc.id)}
                            style={{
                              padding: '0.375rem 0.75rem',
                              border: `1px solid ${ERROR_COLOR}`,
                              background: 'transparent',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              color: ERROR_COLOR,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} style={{ width: 10, height: 10 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

interface ChunksTabProps {
  documents: RagDocument[];
  onViewChunk: (doc: RagDocument, chunkId: string, pageNumber?: number) => void;
}

const CHUNKS_PER_PAGE = 20;

const ChunksTab: React.FC<ChunksTabProps> = ({ documents, onViewChunk }) => {
  const [selectedDocId, setSelectedDocId] = useState<string>('all');
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('all');
  const [chunks, setChunks] = useState<RagDocumentChunk[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // LLM format preview state
  const [llmFormatPreview, setLlmFormatPreview] = useState<string | null>(null);
  const [isLoadingLlmFormat, setIsLoadingLlmFormat] = useState(false);
  const [llmFormatChunkId, setLlmFormatChunkId] = useState<string | null>(null);

  // Handler to fetch and show LLM format for a chunk
  const handleViewLlmFormat = async (e: React.MouseEvent, chunkId: string) => {
    e.stopPropagation(); // Prevent triggering the chunk card click
    setIsLoadingLlmFormat(true);
    setLlmFormatChunkId(chunkId);
    try {
      const result = await apiService.getChunkLlmFormat(chunkId);
      if (result) {
        setLlmFormatPreview(result.llm_format);
      } else {
        setLlmFormatPreview('Failed to load LLM format');
      }
    } catch (error) {
      console.error('Failed to fetch LLM format:', error);
      setLlmFormatPreview('Error loading LLM format');
    } finally {
      setIsLoadingLlmFormat(false);
    }
  };

  const closeLlmFormatModal = () => {
    setLlmFormatPreview(null);
    setLlmFormatChunkId(null);
  };

  const loadChunks = useCallback(async () => {
    setIsLoading(true);
    try {
      const docId = selectedDocId === 'all' ? undefined : selectedDocId;
      const offset = (currentPage - 1) * CHUNKS_PER_PAGE;
      const response = await apiService.getRagChunks(docId, CHUNKS_PER_PAGE, offset);
      setChunks(response.chunks);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error('Failed to load chunks:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDocId, currentPage]);

  useEffect(() => {
    loadChunks();
  }, [loadChunks]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDocId, contentTypeFilter]);

  const filteredChunks = chunks.filter((chunk) => {
    if (contentTypeFilter !== 'all' && chunk.content_type !== contentTypeFilter) return false;
    return true;
  });

  const totalPages = Math.ceil(totalCount / CHUNKS_PER_PAGE);

  // Helper to strip redundant "Summary: " prefix
  const formatSummary = (summary: string): string => {
    if (!summary) return '';
    // Remove leading "Summary: " or "Summary:" if present
    return summary.replace(/^Summary:\s*/i, '');
  };

  // Find document by chunk's document_id
  const findDocumentForChunk = (chunk: RagDocumentChunk): RagDocument | undefined => {
    return documents.find(d => d.id === chunk.document_id);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={selectedDocId}
          onChange={(e) => setSelectedDocId(e.target.value)}
          style={{
            padding: '0.5rem 0.875rem',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            fontSize: '0.875rem',
            background: 'white',
            minWidth: '200px',
            fontFamily: 'Lato, system-ui, sans-serif',
          }}
        >
          <option value="all">All Documents</option>
          {documents.filter((d) => d.status === 'indexed').map((doc) => (
            <option key={doc.id} value={doc.id}>{doc.filename}</option>
          ))}
        </select>
        <select
          value={contentTypeFilter}
          onChange={(e) => setContentTypeFilter(e.target.value)}
          style={{
            padding: '0.5rem 0.875rem',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            fontSize: '0.875rem',
            background: 'white',
            fontFamily: 'Lato, system-ui, sans-serif',
          }}
        >
          <option value="all">All Content Types</option>
          <option value="paragraph">Paragraph</option>
          <option value="table">Table</option>
        </select>
        {/* Pagination controls */}
        {totalPages > 1 && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.375rem 0.75rem',
                border: '1px solid #e2e8f0',
                background: currentPage === 1 ? '#f1f5f9' : 'white',
                borderRadius: '4px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.8rem',
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.375rem 0.75rem',
                border: '1px solid #e2e8f0',
                background: currentPage === totalPages ? '#f1f5f9' : 'white',
                borderRadius: '4px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '0.8rem',
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <SectionCard title={`Chunks (${totalCount.toLocaleString()})`} icon={faFileAlt}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
            Loading chunks...
          </div>
        ) : filteredChunks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            No chunks found.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredChunks.map((chunk) => {
              const doc = findDocumentForChunk(chunk);
              return (
                <div
                  key={chunk.id}
                  onClick={() => doc && onViewChunk(doc, chunk.id, chunk.page_number)}
                  style={{
                    background: '#f8fafc',
                    borderRadius: '4px',
                    padding: '1rem',
                    cursor: doc ? 'pointer' : 'default',
                    transition: 'all 0.15s',
                    border: '1px solid transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (doc) {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  {/* Header row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.125rem 0.5rem',
                          background: chunk.content_type === 'table' ? PURPLE_COLOR : INFO_COLOR,
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                        }}
                      >
                        {chunk.content_type}
                      </span>
                      <strong style={{ fontSize: '0.875rem', color: DARK_COLOR }}>{chunk.filename}</strong>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <span>Chunk #{chunk.chunk_index}</span>
                      {chunk.page_number && <span>Page {chunk.page_number}</span>}
                      <span>{chunk.summary_tokens} tokens</span>
                      {doc && (
                        <span style={{
                          fontSize: '0.7rem',
                          color: 'white',
                          background: INFO_COLOR,
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}>
                          <FontAwesomeIcon icon={faEye} style={{ width: 10 }} />
                          View
                        </span>
                      )}
                      <button
                        onClick={(e) => handleViewLlmFormat(e, chunk.id)}
                        disabled={isLoadingLlmFormat && llmFormatChunkId === chunk.id}
                        style={{
                          fontSize: '0.7rem',
                          color: 'white',
                          background: '#8b5cf6',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          border: 'none',
                          cursor: 'pointer',
                          opacity: isLoadingLlmFormat && llmFormatChunkId === chunk.id ? 0.7 : 1,
                        }}
                      >
                        {isLoadingLlmFormat && llmFormatChunkId === chunk.id ? (
                          <FontAwesomeIcon icon={faSpinner} spin style={{ width: 10 }} />
                        ) : (
                          <FontAwesomeIcon icon={faRobot} style={{ width: 10 }} />
                        )}
                        LLM View
                      </button>
                    </div>
                  </div>

                  {/* Heading context */}
                  {chunk.heading_context && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6366f1',
                      fontWeight: 500,
                      marginBottom: '0.5rem',
                      padding: '0.25rem 0.5rem',
                      background: '#eef2ff',
                      borderRadius: '4px',
                      display: 'inline-block',
                    }}>
                      {chunk.heading_context}
                    </div>
                  )}

                  {/* Summary */}
                  {chunk.summary && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: 600 }}>
                        Summary
                      </div>
                      <div style={{
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                        color: '#1e293b',
                        background: '#e0f2fe',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '4px',
                      }}>
                        {formatSummary(chunk.summary)}
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div>
                    <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: 600 }}>
                      Content
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      lineHeight: 1.5,
                      color: '#334155',
                      background: 'white',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #e2e8f0',
                      maxHeight: '80px',
                      overflow: 'hidden',
                    }}>
                      {chunk.raw_content || '(No content available)'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* LLM Format Preview Modal */}
      {llmFormatPreview !== null && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeLlmFormatModal}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '4px',
              padding: '1.5rem',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: DARK_COLOR, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FontAwesomeIcon icon={faRobot} style={{ color: '#8b5cf6' }} />
                LLM View
              </h3>
              <button
                onClick={closeLlmFormatModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: '#64748b',
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
              This is the exact text the LLM receives when this chunk is returned from a search:
            </p>
            <pre
              style={{
                background: '#1e293b',
                color: '#e2e8f0',
                padding: '1rem',
                borderRadius: '4px',
                fontSize: '0.8rem',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
                margin: 0,
                overflow: 'auto',
              }}
            >
              {llmFormatPreview}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

interface SearchLabTabProps {
  vectorWeight: number;
  onVectorWeightChange: (weight: number) => void;
  useSemanticRanker: boolean;
  onSemanticRankerChange: (enabled: boolean) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  searchResults: RagSearchResult[];
  isSearching: boolean;
  onSearch: () => void;
  documents: RagDocument[];
  onViewInDocument: (doc: RagDocument, chunkId: string, pageNumber?: number) => void;
}

const SearchLabTab: React.FC<SearchLabTabProps> = ({
  vectorWeight,
  onVectorWeightChange,
  useSemanticRanker,
  onSemanticRankerChange,
  searchQuery,
  onSearchQueryChange,
  searchResults,
  isSearching,
  onSearch,
  documents,
  onViewInDocument,
}) => {
  // Find document by ID
  const findDocument = (documentId: string): RagDocument | undefined => {
    return documents.find(d => d.id === documentId);
  };

  // LLM format preview state
  const [llmFormatPreview, setLlmFormatPreview] = useState<string | null>(null);
  const [isLoadingLlmFormat, setIsLoadingLlmFormat] = useState(false);
  const [llmFormatChunkId, setLlmFormatChunkId] = useState<string | null>(null);

  // Handler to fetch and show LLM format for a search result
  const handleViewLlmFormat = async (e: React.MouseEvent, chunkId: string) => {
    e.stopPropagation();
    setIsLoadingLlmFormat(true);
    setLlmFormatChunkId(chunkId);
    try {
      const result = await apiService.getChunkLlmFormat(chunkId);
      if (result) {
        setLlmFormatPreview(result.llm_format);
      } else {
        setLlmFormatPreview('Failed to load LLM format');
      }
    } catch (error) {
      console.error('Failed to fetch LLM format:', error);
      setLlmFormatPreview('Error loading LLM format');
    } finally {
      setIsLoadingLlmFormat(false);
    }
  };

  const closeLlmFormatModal = () => {
    setLlmFormatPreview(null);
    setLlmFormatChunkId(null);
  };

  return (
  <div>
    <SectionCard title="Search Configuration" icon={faSliders}>
      {/* Semantic Ranker Toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
        background: useSemanticRanker ? '#f0fdf4' : '#f8fafc',
        borderRadius: '4px',
        marginBottom: '1.5rem',
        border: useSemanticRanker ? `1px solid ${SUCCESS_COLOR}` : '1px solid #e2e8f0',
      }}>
        <div>
          <div style={{ fontSize: '0.9rem', fontWeight: 500, color: DARK_COLOR }}>
            Azure Semantic Ranker (L2 Reranking)
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
            Uses Microsoft transformer model to rerank by semantic understanding.
            {useSemanticRanker && ' Results include reranker_score (0-4).'}
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={useSemanticRanker}
            onChange={(e) => onSemanticRankerChange(e.target.checked)}
            style={{ width: '20px', height: '20px', accentColor: SUCCESS_COLOR, cursor: 'pointer' }}
          />
          <span style={{
            marginLeft: '0.5rem',
            fontSize: '0.8rem',
            fontWeight: 500,
            color: useSemanticRanker ? SUCCESS_COLOR : '#64748b',
          }}>
            {useSemanticRanker ? 'Enabled' : 'Disabled'}
          </span>
        </label>
      </div>
      {/* Vector Weight Slider */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Vector Weight (Semantic Matching)</span>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: ACCENT_COLOR }}>
            {(vectorWeight * 100).toFixed(0)}%
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={vectorWeight}
          onChange={(e) => {
            onVectorWeightChange(parseFloat(e.target.value));
          }}
          style={{ width: '100%', accentColor: ACCENT_COLOR }}
        />
        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
          Adjusts vector influence in Azure's RRF hybrid scoring. Higher = more semantic matching vs keyword.
        </div>
      </div>
    </SectionCard>

    <SectionCard title="Test Search Query" icon={faSearch}>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <input
          type="text"
          placeholder="Enter a test search query..."
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSearch();
          }}
          style={{
            flex: 1,
            padding: '0.625rem 1rem',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontFamily: 'Lato, system-ui, sans-serif',
          }}
        />
        <ActionButton onClick={onSearch} icon={faSearch} variant="primary" loading={isSearching}>
          Search
        </ActionButton>
      </div>

      {searchResults.length > 0 && (
        <div>
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
            Showing {searchResults.length} results
          </div>
          {searchResults.map((result, index) => (
            <div
              key={result.id || index}
              style={{
                background: '#f8fafc',
                borderRadius: '4px',
                padding: '1rem',
                marginBottom: '0.75rem',
                borderLeft: `3px solid ${ACCENT_COLOR}`,
              }}
            >
              {/* Header row with score and metadata */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'white',
                    background: ACCENT_COLOR,
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                  }}>
                    Score: {result.score.toFixed(4)}
                  </span>
                  {result.reranker_score !== undefined && (
                    <span style={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'white',
                      background: SUCCESS_COLOR,
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                    }}>
                      Rerank: {result.reranker_score.toFixed(2)}
                    </span>
                  )}
                  <span style={{
                    fontSize: '0.65rem',
                    color: 'white',
                    background: result.content_type === 'table' ? '#8b5cf6' : '#3b82f6',
                    padding: '0.2rem 0.4rem',
                    borderRadius: '4px',
                    textTransform: 'uppercase',
                  }}>
                    {result.content_type}
                  </span>
                  <strong style={{ fontSize: '0.8rem', color: DARK_COLOR }}>{result.filename}</strong>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <span>Chunk #{result.chunk_index}</span>
                  {result.page_number && <span>Page {result.page_number}</span>}
                  {(() => {
                    const doc = findDocument(result.document_id);
                    if (!doc) return null;
                    return (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewInDocument(doc, result.id, result.page_number);
                        }}
                        style={{
                          fontSize: '0.7rem',
                          color: 'white',
                          background: INFO_COLOR,
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          cursor: 'pointer',
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} style={{ width: 10 }} />
                        View
                      </span>
                    );
                  })()}
                  <button
                    onClick={(e) => handleViewLlmFormat(e, result.id)}
                    disabled={isLoadingLlmFormat && llmFormatChunkId === result.id}
                    style={{
                      fontSize: '0.7rem',
                      color: 'white',
                      background: '#8b5cf6',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      border: 'none',
                      cursor: 'pointer',
                      opacity: isLoadingLlmFormat && llmFormatChunkId === result.id ? 0.7 : 1,
                    }}
                  >
                    {isLoadingLlmFormat && llmFormatChunkId === result.id ? (
                      <FontAwesomeIcon icon={faSpinner} spin style={{ width: 10 }} />
                    ) : (
                      <FontAwesomeIcon icon={faRobot} style={{ width: 10 }} />
                    )}
                    LLM View
                  </button>
                </div>
              </div>

              {/* Heading context if available */}
              {result.heading_context && (
                <div style={{
                  fontSize: '0.75rem',
                  color: '#6366f1',
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  background: '#eef2ff',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}>
                  📑 {result.heading_context}
                </div>
              )}

              {/* Summary section */}
              {result.summary && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: 600 }}>
                    Summary (used for search matching)
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    color: '#1e293b',
                    background: '#e0f2fe',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '4px',
                    borderLeft: '2px solid #0ea5e9',
                  }}>
                    {result.summary}
                  </div>
                </div>
              )}

              {/* Full content section */}
              <div>
                <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: 600 }}>
                  Original Content
                </div>
                <div style={{
                  fontSize: '0.8rem',
                  lineHeight: 1.6,
                  color: '#334155',
                  background: 'white',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  maxHeight: '150px',
                  overflow: 'auto',
                }}>
                  {result.content}
                </div>
              </div>

            </div>
          ))}
        </div>
      )}
    </SectionCard>

    {/* LLM Format Preview Modal */}
    {llmFormatPreview !== null && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={closeLlmFormatModal}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '4px',
            padding: '1.5rem',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: DARK_COLOR, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faRobot} style={{ color: '#8b5cf6' }} />
              LLM View
            </h3>
            <button
              onClick={closeLlmFormatModal}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                color: '#64748b',
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
            This is the exact text the LLM receives when this chunk is returned from a search:
          </p>
          <pre
            style={{
              background: '#1e293b',
              color: '#e2e8f0',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
              margin: 0,
              overflow: 'auto',
            }}
          >
            {llmFormatPreview}
          </pre>
        </div>
      </div>
    )}
  </div>
  );
};

interface ErrorsTabProps {
  documents: RagDocument[];
  onRetry: (id: string) => void;
  onDelete: (id: string) => void;
  onRetryAll: () => void;
  retryingDocumentId?: string | null;
  retryStatus?: string | null;
}

const ErrorsTab: React.FC<ErrorsTabProps> = ({ documents, onRetry, onDelete, onRetryAll, retryingDocumentId, retryStatus }) => {
  const failedDocs = documents.filter((d) => d.status === 'failed' || isStaleProcessing(d));

  return (
    <SectionCard
      title={`Failed Documents (${failedDocs.length})`}
      icon={faExclamationTriangle}
      action={
        failedDocs.length > 0 && (
          <ActionButton onClick={onRetryAll} icon={faRedo} variant="primary">
            Retry All
          </ActionButton>
        )
      }
    >
      {failedDocs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: SUCCESS_COLOR }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
          No failed documents!
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {failedDocs.map((doc) => (
            <div
              key={doc.id}
              style={{
                background: '#fef2f2',
                borderRadius: '4px',
                padding: '1rem',
                borderLeft: `3px solid ${ERROR_COLOR}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 500, color: DARK_COLOR }}>{doc.filename}</div>
                <span
                  style={{
                    fontSize: '0.7rem',
                    padding: '0.125rem 0.5rem',
                    background: ERROR_COLOR,
                    color: 'white',
                    borderRadius: '4px',
                  }}
                >
                  {isStaleProcessing(doc) ? 'STALE' : 'FAILED'}
                </span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#b91c1c', marginBottom: '0.5rem' }}>
                {doc.error_message || 'Processing timeout - document has been processing for over 6 hours'}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <ActionButton
                  onClick={() => onRetry(doc.id)}
                  icon={faRedo}
                  variant="primary"
                  loading={retryingDocumentId === doc.id}
                  disabled={retryingDocumentId === doc.id}
                >
                  {retryingDocumentId === doc.id ? (retryStatus || 'Retrying...') : 'Retry'}
                </ActionButton>
                <ActionButton onClick={() => onDelete(doc.id)} icon={faTrash} variant="danger">
                  Delete
                </ActionButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ============================================================================
// Tests Tab
// ============================================================================

type TestsSubView = 'sets' | 'set-detail' | 'runs' | 'run-detail' | 'questions';

interface TestsTabProps {
  documents: RagDocument[];
}

const TestsTab: React.FC<TestsTabProps> = ({ documents }) => {
  // Sub-view navigation
  const [subView, setSubView] = useState<TestsSubView>('sets');
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  // Data state
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [allQuestions, setAllQuestions] = useState<TestQuestion[]>([]);
  const [selectedSet, setSelectedSet] = useState<TestSetWithQuestions | null>(null);
  const [selectedRun, setSelectedRun] = useState<TestRunWithResults | null>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Modal states
  const [showCreateSetModal, setShowCreateSetModal] = useState(false);
  const [showRunTestModal, setShowRunTestModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<string[]>([]);
  const [selectedQuestionResult, setSelectedQuestionResult] = useState<QuestionResult | null>(null);

  // Form states
  const [newSetName, setNewSetName] = useState('');
  const [newSetDescription, setNewSetDescription] = useState('');
  const [runConfig, setRunConfig] = useState<TestRunConfig>({
    search: { search_limit: 10, vector_weight: 0.75, use_semantic_ranker: false, include_reasoning: true },
    evaluation: { model: 'gpt-4.1-mini', parallel_workers: 5 }
  });

  // Generate questions state
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [questionsPerPage, setQuestionsPerPage] = useState(3);
  const [generateJobId, setGenerateJobId] = useState<string | null>(null);
  const [generateStatus, setGenerateStatus] = useState<string | null>(null);

  // Run job polling
  const [runJobId, setRunJobId] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<string | null>(null);

  // Question filter
  const [questionSearch, setQuestionSearch] = useState('');
  const [resultFilter, setResultFilter] = useState<'all' | 'passed' | 'failed'>('all');

  // Load test sets
  const loadTestSets = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getTestSets();
      setTestSets(response.test_sets);
    } catch (error) {
      console.error('Failed to load test sets:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load test runs
  const loadTestRuns = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getTestRuns();
      setTestRuns(response.test_runs);
    } catch (error) {
      console.error('Failed to load test runs:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load all questions
  const loadAllQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getTestQuestions(500, 0);
      setAllQuestions(response.questions);
    } catch (error) {
      console.error('Failed to load questions:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load test set detail
  const loadTestSetDetail = useCallback(async (setId: string) => {
    setIsLoading(true);
    try {
      const data = await apiService.getTestSet(setId);
      setSelectedSet(data);
    } catch (error) {
      console.error('Failed to load test set:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load test run detail
  const loadTestRunDetail = useCallback(async (runId: string) => {
    setIsLoading(true);
    try {
      const data = await apiService.getTestRun(runId);
      setSelectedRun(data);
    } catch (error) {
      console.error('Failed to load test run:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (subView === 'sets') loadTestSets();
    if (subView === 'runs') loadTestRuns();
    if (subView === 'questions') loadAllQuestions();
  }, [subView, loadTestSets, loadTestRuns, loadAllQuestions]);

  // Load detail when selected
  useEffect(() => {
    if (subView === 'set-detail' && selectedSetId) {
      loadTestSetDetail(selectedSetId);
      loadAllQuestions(); // Load questions for "Add Existing" modal
    }
  }, [subView, selectedSetId, loadTestSetDetail, loadAllQuestions]);

  useEffect(() => {
    if (subView === 'run-detail' && selectedRunId) {
      loadTestRunDetail(selectedRunId);
    }
  }, [subView, selectedRunId, loadTestRunDetail]);

  // Handlers
  const handleCreateSet = async () => {
    if (!newSetName.trim()) return;
    setIsCreating(true);
    try {
      await apiService.createTestSet({
        name: newSetName.trim(),
        description: newSetDescription.trim() || undefined,
      });
      setShowCreateSetModal(false);
      setNewSetName('');
      setNewSetDescription('');
      loadTestSets();
    } catch (error) {
      console.error('Failed to create test set:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSet = async (setId: string) => {
    if (!confirm('Delete this test set? This will not delete the questions.')) return;
    try {
      await apiService.deleteTestSet(setId);
      loadTestSets();
    } catch (error) {
      console.error('Failed to delete test set:', error);
    }
  };

  const handleViewSet = (setId: string) => {
    setSelectedSetId(setId);
    setSubView('set-detail');
  };

  const handleViewRun = (runId: string) => {
    setSelectedRunId(runId);
    setSubView('run-detail');
  };

  const handleStartRun = async () => {
    if (!selectedSetId) return;
    setIsCreating(true);
    try {
      const result = await apiService.createTestRun({
        test_set_id: selectedSetId,
        config: runConfig,
      });
      if (result) {
        setRunJobId(result.job_id);
        setRunStatus('pending');
        setShowRunTestModal(false);
        // Start polling for job status
        pollJobStatus(result.job_id, 'run', result.run_id);
      }
    } catch (error) {
      console.error('Failed to start test run:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!selectedSetId || selectedDocIds.length === 0) return;
    setIsCreating(true);
    try {
      const result = await apiService.generateTestQuestions(selectedSetId, {
        document_ids: selectedDocIds,
        questions_per_page: questionsPerPage,
      });
      if (result) {
        setGenerateJobId(result.job_id);
        setGenerateStatus('pending');
        setShowGenerateModal(false);
        // Start polling for job status
        pollJobStatus(result.job_id, 'generate');
      }
    } catch (error) {
      console.error('Failed to generate questions:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const pollJobStatus = async (jobId: string, type: 'run' | 'generate', runId?: string) => {
    const poll = async () => {
      try {
        const status = await apiService.getTestJobStatus(jobId);
        if (!status) return;

        if (type === 'generate') {
          setGenerateStatus(status.status);
        } else {
          setRunStatus(status.status);
        }

        if (status.status === 'completed') {
          if (type === 'generate') {
            setGenerateJobId(null);
            setGenerateStatus(null);
            loadTestSetDetail(selectedSetId!);
          } else {
            setRunJobId(null);
            setRunStatus(null);
            if (runId) {
              setSelectedRunId(runId);
              setSubView('run-detail');
            }
          }
          return;
        }

        if (status.status === 'failed') {
          if (type === 'generate') {
            setGenerateStatus(`Failed: ${status.error}`);
          } else {
            setRunStatus(`Failed: ${status.error}`);
          }
          return;
        }

        // Continue polling
        setTimeout(poll, 2000);
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };
    poll();
  };

  const handleRemoveQuestion = async (questionId: string) => {
    if (!selectedSetId) return;
    try {
      await apiService.updateTestSet(selectedSetId, {
        remove_question_ids: [questionId],
      });
      loadTestSetDetail(selectedSetId);
    } catch (error) {
      console.error('Failed to remove question:', error);
    }
  };

  const handleAddQuestions = async (questionIds: string[]) => {
    if (!selectedSetId || questionIds.length === 0) return;
    try {
      await apiService.updateTestSet(selectedSetId, {
        add_question_ids: questionIds,
      });
      setShowAddQuestionModal(false);
      loadTestSetDetail(selectedSetId);
    } catch (error) {
      console.error('Failed to add questions:', error);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Delete this question permanently?')) return;
    try {
      await apiService.deleteTestQuestion(questionId);
      loadAllQuestions();
    } catch (error) {
      console.error('Failed to delete question:', error);
    }
  };

  // Sub-navigation tabs
  const subTabs = [
    { id: 'sets' as TestsSubView, label: 'Test Sets', icon: faListCheck },
    { id: 'runs' as TestsSubView, label: 'Test Runs', icon: faPlay },
    { id: 'questions' as TestsSubView, label: 'Question Pool', icon: faQuestionCircle },
  ];

  // Indexed documents for selection
  const indexedDocs = documents.filter(d => d.status === 'indexed');

  // Format percentage
  const formatPercent = (value?: number) => value !== undefined ? `${(value * 100).toFixed(1)}%` : '-';

  // ============================================================================
  // Render Sub-views
  // ============================================================================

  // Test Sets List
  const renderSetsView = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
          {testSets.length} test set{testSets.length !== 1 ? 's' : ''}
        </div>
        <ActionButton onClick={() => setShowCreateSetModal(true)} icon={faPlus} variant="primary">
          New Test Set
        </ActionButton>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
          <FontAwesomeIcon icon={faSpinner} spin /> Loading...
        </div>
      ) : testSets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
          No test sets yet. Create one to get started.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {testSets.map(set => (
            <div
              key={set.id}
              style={{
                background: 'white',
                borderRadius: '4px',
                padding: '1.25rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
              }}
              onClick={() => handleViewSet(set.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, color: DARK_COLOR, marginBottom: '0.25rem' }}>
                    {set.name}
                  </div>
                  {set.description && (
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                      {set.description}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    {set.question_count} question{set.question_count !== 1 ? 's' : ''} • Created {formatDate(set.created_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteSet(set.id); }}
                    style={{
                      padding: '0.375rem',
                      border: 'none',
                      background: 'transparent',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      borderRadius: '4px',
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Test Set Detail
  const renderSetDetailView = () => {
    if (!selectedSet) return <div style={{ textAlign: 'center', padding: '2rem' }}><FontAwesomeIcon icon={faSpinner} spin /></div>;

    return (
      <div>
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => { setSubView('sets'); setSelectedSet(null); }}
            style={{
              background: 'none',
              border: 'none',
              color: ACCENT_COLOR,
              cursor: 'pointer',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: 0,
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Test Sets
          </button>
        </div>

        <SectionCard
          title={selectedSet.name}
          icon={faListCheck}
          action={
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <ActionButton onClick={() => setShowGenerateModal(true)} icon={faPlus} disabled={!!generateJobId}>
                Generate Questions
              </ActionButton>
              <ActionButton onClick={() => { setSelectedToAdd([]); setShowAddQuestionModal(true); }} icon={faPlus}>
                Add Existing
              </ActionButton>
              <ActionButton
                onClick={() => { setShowRunTestModal(true); }}
                icon={faPlay}
                variant="primary"
                disabled={selectedSet.questions.length === 0}
              >
                Run Test
              </ActionButton>
            </div>
          }
        >
          {generateJobId && (
            <div style={{
              padding: '0.75rem',
              background: '#fffbeb',
              borderRadius: '4px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: '#92400e',
            }}>
              <FontAwesomeIcon icon={faSpinner} spin />
              Generating questions... ({generateStatus})
            </div>
          )}

          {selectedSet.description && (
            <div style={{ marginBottom: '1rem', color: '#64748b', fontSize: '0.875rem' }}>
              {selectedSet.description}
            </div>
          )}

          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
            {selectedSet.questions.length} question{selectedSet.questions.length !== 1 ? 's' : ''}
          </div>

          {selectedSet.questions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
              No questions in this test set. Generate or add some questions.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {selectedSet.questions.map((q, idx) => (
                <div
                  key={q.id}
                  style={{
                    padding: '1rem',
                    background: '#f8fafc',
                    borderRadius: '4px',
                    borderLeft: `3px solid ${ACCENT_COLOR}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                        Q{idx + 1}
                      </div>
                      <div style={{ fontWeight: 500, color: DARK_COLOR, marginBottom: '0.5rem' }}>
                        {q.query}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        <strong>Expected:</strong> {q.expected_answer}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveQuestion(q.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        padding: '0.25rem',
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    );
  };

  // Test Runs List
  const renderRunsView = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
          {testRuns.length} test run{testRuns.length !== 1 ? 's' : ''}
        </div>
        <ActionButton onClick={loadTestRuns} icon={faSync}>
          Refresh
        </ActionButton>
      </div>

      {runJobId && (
        <div style={{
          padding: '0.75rem 1rem',
          background: '#fffbeb',
          borderRadius: '4px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.875rem',
          color: '#92400e',
        }}>
          <FontAwesomeIcon icon={faSpinner} spin />
          Running test... ({runStatus})
        </div>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
          <FontAwesomeIcon icon={faSpinner} spin /> Loading...
        </div>
      ) : testRuns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
          No test runs yet. Open a test set and click "Run Test".
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '4px', overflow: 'hidden' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Test Set</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Results</th>
                <th style={{ textAlign: 'center', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Hit Rate</th>
                <th style={{ textAlign: 'center', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>MRR</th>
                <th style={{ textAlign: 'center', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Cost</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Config</th>
                <th style={{ textAlign: 'right', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {testRuns.map(run => (
                <tr
                  key={run.id}
                  onClick={() => run.status === 'completed' && handleViewRun(run.id)}
                  style={{ cursor: run.status === 'completed' ? 'pointer' : 'default' }}
                >
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontWeight: 500, color: DARK_COLOR }}>{run.test_set_name || 'Unknown'}</div>
                    {run.name && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{run.name}</div>}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.625rem',
                      borderRadius: '9999px',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      backgroundColor: run.status === 'completed' ? `${SUCCESS_COLOR}20` :
                        run.status === 'running' ? `${WARNING_COLOR}20` :
                          run.status === 'failed' ? `${ERROR_COLOR}20` : '#f1f5f9',
                      color: run.status === 'completed' ? SUCCESS_COLOR :
                        run.status === 'running' ? WARNING_COLOR :
                          run.status === 'failed' ? ERROR_COLOR : '#64748b',
                    }}>
                      {run.status === 'running' && <FontAwesomeIcon icon={faSpinner} spin style={{ width: 10, height: 10 }} />}
                      {run.status}
                    </span>
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                    {run.stats ? (
                      <span>
                        <span style={{ color: SUCCESS_COLOR, fontWeight: 600 }}>{run.stats.questions_passed}</span>
                        <span style={{ color: '#94a3b8' }}> / </span>
                        <span>{run.stats.total_questions}</span>
                        <span style={{ color: '#94a3b8', fontSize: '0.7rem', marginLeft: '0.25rem' }}>passed</span>
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9', textAlign: 'center', fontWeight: 500, color: run.stats?.hit_rate ? (run.stats.hit_rate >= 0.8 ? SUCCESS_COLOR : run.stats.hit_rate >= 0.5 ? WARNING_COLOR : ERROR_COLOR) : '#64748b' }}>
                    {formatPercent(run.stats?.hit_rate)}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9', textAlign: 'center', color: '#64748b' }}>
                    {run.stats?.mrr?.toFixed(3) || '-'}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9', textAlign: 'center', color: INFO_COLOR, fontWeight: 500 }}>
                    {run.stats?.evaluation_cost_usd ? `$${run.stats.evaluation_cost_usd.toFixed(4)}` : '-'}
                  </td>
                  <td style={{ padding: '0.5rem 1rem', fontSize: '0.7rem', borderBottom: '1px solid #f1f5f9' }}>
                    {run.config ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {run.config.evaluation?.model && (
                          <span style={{
                            padding: '0.125rem 0.375rem',
                            background: `${ACCENT_COLOR}15`,
                            borderRadius: '4px',
                            border: `1px solid ${ACCENT_COLOR}40`,
                            color: ACCENT_COLOR,
                            fontWeight: 500,
                          }}>
                            {run.config.evaluation.model}
                          </span>
                        )}
                        {run.config.search?.vector_weight !== undefined && (
                          <span style={{
                            padding: '0.125rem 0.375rem',
                            background: '#f1f5f9',
                            borderRadius: '4px',
                            color: '#64748b',
                          }}>
                            V:{(run.config.search.vector_weight * 100).toFixed(0)}%
                          </span>
                        )}
                        {run.config.search?.use_semantic_ranker && (
                          <span style={{
                            padding: '0.125rem 0.375rem',
                            background: `${SUCCESS_COLOR}15`,
                            borderRadius: '4px',
                            color: SUCCESS_COLOR,
                          }}>
                            SR
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#cbd5e1' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#94a3b8' }}>
                    {formatDate(run.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Test Run Detail
  const renderRunDetailView = () => {
    if (!selectedRun) return <div style={{ textAlign: 'center', padding: '2rem' }}><FontAwesomeIcon icon={faSpinner} spin /></div>;

    const results = selectedRun.results?.questions || [];
    const filteredResults = results.filter(r => {
      if (resultFilter === 'passed') return r.found;
      if (resultFilter === 'failed') return !r.found;
      return true;
    }).filter(r =>
      questionSearch ? r.query.toLowerCase().includes(questionSearch.toLowerCase()) : true
    );

    return (
      <div>
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => { setSubView('runs'); setSelectedRun(null); }}
            style={{
              background: 'none',
              border: 'none',
              color: ACCENT_COLOR,
              cursor: 'pointer',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: 0,
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Test Runs
          </button>
        </div>

        {/* Stats Summary */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}>
          <StatCard label="Total Questions" value={selectedRun.stats?.total_questions || 0} />
          <StatCard label="Passed" value={selectedRun.stats?.questions_passed || 0} color={SUCCESS_COLOR} />
          <StatCard label="Failed" value={selectedRun.stats?.questions_failed || 0} color={ERROR_COLOR} />
          <StatCard label="Hit Rate" value={formatPercent(selectedRun.stats?.hit_rate)} color={selectedRun.stats?.hit_rate && selectedRun.stats.hit_rate >= 0.8 ? SUCCESS_COLOR : WARNING_COLOR} />
          <StatCard label="MRR" value={selectedRun.stats?.mrr?.toFixed(3) || '-'} />
          <StatCard label="Cost" value={selectedRun.stats?.evaluation_cost_usd ? `$${selectedRun.stats.evaluation_cost_usd.toFixed(4)}` : '-'} color={INFO_COLOR} />
        </div>

        {/* Run Configuration */}
        {selectedRun.config && (
          <div style={{
            background: '#f8fafc',
            borderRadius: '4px',
            padding: '1rem',
            marginBottom: '1.5rem',
            border: '1px solid #e2e8f0',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.75rem',
              color: DARK_COLOR,
              fontWeight: 600,
              fontSize: '0.875rem',
            }}>
              <FontAwesomeIcon icon={faCog} style={{ color: '#64748b' }} />
              Run Configuration
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
              {/* Search Config */}
              {selectedRun.config.search && (
                <div>
                  <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '0.375rem', letterSpacing: '0.05em' }}>Search</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {selectedRun.config.search.search_limit !== undefined && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: 'white',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        border: '1px solid #e2e8f0',
                      }}>
                        <span style={{ color: '#64748b' }}>Limit:</span> <span style={{ fontWeight: 500 }}>{selectedRun.config.search.search_limit}</span>
                      </span>
                    )}
                    {selectedRun.config.search.vector_weight !== undefined && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: 'white',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        border: '1px solid #e2e8f0',
                      }}>
                        <span style={{ color: '#64748b' }}>Vector:</span> <span style={{ fontWeight: 500 }}>{(selectedRun.config.search.vector_weight * 100).toFixed(0)}%</span>
                      </span>
                    )}
                    {selectedRun.config.search.use_semantic_ranker !== undefined && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: selectedRun.config.search.use_semantic_ranker ? `${SUCCESS_COLOR}15` : 'white',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        border: `1px solid ${selectedRun.config.search.use_semantic_ranker ? SUCCESS_COLOR : '#e2e8f0'}`,
                        color: selectedRun.config.search.use_semantic_ranker ? SUCCESS_COLOR : '#64748b',
                      }}>
                        Semantic Ranker: {selectedRun.config.search.use_semantic_ranker ? 'On' : 'Off'}
                      </span>
                    )}
                    {selectedRun.config.search.include_reasoning !== undefined && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: selectedRun.config.search.include_reasoning ? `${INFO_COLOR}15` : 'white',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        border: `1px solid ${selectedRun.config.search.include_reasoning ? INFO_COLOR : '#e2e8f0'}`,
                        color: selectedRun.config.search.include_reasoning ? INFO_COLOR : '#64748b',
                      }}>
                        Reasoning: {selectedRun.config.search.include_reasoning ? 'On' : 'Off'}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {/* Evaluation Config */}
              {selectedRun.config.evaluation && (
                <div>
                  <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '0.375rem', letterSpacing: '0.05em' }}>Evaluation</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {selectedRun.config.evaluation.model && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: `${ACCENT_COLOR}15`,
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        border: `1px solid ${ACCENT_COLOR}`,
                        color: ACCENT_COLOR,
                        fontWeight: 500,
                      }}>
                        {selectedRun.config.evaluation.model}
                      </span>
                    )}
                    {selectedRun.config.evaluation.parallel_workers !== undefined && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: 'white',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        border: '1px solid #e2e8f0',
                      }}>
                        <span style={{ color: '#64748b' }}>Workers:</span> <span style={{ fontWeight: 500 }}>{selectedRun.config.evaluation.parallel_workers}</span>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        <SectionCard
          title="Question Results"
          icon={faQuestionCircle}
          action={
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search questions..."
                value={questionSearch}
                onChange={(e) => setQuestionSearch(e.target.value)}
                style={{
                  padding: '0.375rem 0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                }}
              />
              <select
                value={resultFilter}
                onChange={(e) => setResultFilter(e.target.value as 'all' | 'passed' | 'failed')}
                style={{
                  padding: '0.375rem 0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  background: 'white',
                }}
              >
                <option value="all">All Results</option>
                <option value="passed">Passed Only</option>
                <option value="failed">Failed Only</option>
              </select>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredResults.map((result) => (
              <div
                key={result.question_id}
                onClick={() => setSelectedQuestionResult(result)}
                style={{
                  padding: '1rem',
                  background: '#f8fafc',
                  borderRadius: '4px',
                  borderLeft: `3px solid ${result.found ? SUCCESS_COLOR : ERROR_COLOR}`,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.transform = 'translateX(2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.transform = 'translateX(0)'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div style={{ fontWeight: 500, color: DARK_COLOR, flex: 1 }}>
                    {result.query}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      backgroundColor: result.found ? `${SUCCESS_COLOR}20` : `${ERROR_COLOR}20`,
                      color: result.found ? SUCCESS_COLOR : ERROR_COLOR,
                    }}>
                      {result.found ? 'FOUND' : 'NOT FOUND'}
                    </span>
                    <FontAwesomeIcon icon={faChevronRight} style={{ color: '#94a3b8', fontSize: '0.7rem' }} />
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                  <strong>Expected:</strong> {result.expected_answer.length > 100 ? result.expected_answer.substring(0, 100) + '...' : result.expected_answer}
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.7rem', color: '#94a3b8' }}>
                  <span>Chunks: {result.chunks_retrieved}</span>
                  {result.top_chunk_score && <span>Top Score: {result.top_chunk_score.toFixed(3)}</span>}
                  {result.search_latency_ms && <span>Search: {result.search_latency_ms}ms</span>}
                  {result.confidence !== undefined && <span>Confidence: {(result.confidence * 100).toFixed(0)}%</span>}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    );
  };

  // Question Pool
  const renderQuestionsView = () => {
    const filteredQuestions = allQuestions.filter(q =>
      questionSearch ? q.query.toLowerCase().includes(questionSearch.toLowerCase()) ||
        q.expected_answer.toLowerCase().includes(questionSearch.toLowerCase()) : true
    );

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search questions..."
            value={questionSearch}
            onChange={(e) => setQuestionSearch(e.target.value)}
            style={{
              padding: '0.5rem 0.875rem',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '0.875rem',
              minWidth: '250px',
            }}
          />
          <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
            {filteredQuestions.length} of {allQuestions.length} questions
          </div>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading...
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
            No questions found.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredQuestions.slice(0, 50).map((q) => (
              <div
                key={q.id}
                style={{
                  padding: '1rem',
                  background: 'white',
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: DARK_COLOR, marginBottom: '0.5rem' }}>
                      {q.query}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      <strong>Expected:</strong> {q.expected_answer}
                    </div>
                    {q.source_page_number && (
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                        Source: Page {q.source_page_number}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteQuestion(q.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      padding: '0.25rem',
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
            {filteredQuestions.length > 50 && (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontSize: '0.875rem' }}>
                Showing 50 of {filteredQuestions.length} questions
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ============================================================================
  // Modals
  // ============================================================================

  // Create Set Modal
  const renderCreateSetModal = () => showCreateSetModal && (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }} onClick={() => setShowCreateSetModal(false)}>
      <div style={{
        background: 'white',
        borderRadius: '4px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '500px',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 1rem', color: DARK_COLOR }}>Create Test Set</h3>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#64748b', marginBottom: '0.25rem' }}>Name</label>
          <input
            type="text"
            value={newSetName}
            onChange={(e) => setNewSetName(e.target.value)}
            placeholder="e.g., Policy Documents Test"
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '0.875rem',
            }}
          />
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#64748b', marginBottom: '0.25rem' }}>Description (optional)</label>
          <textarea
            value={newSetDescription}
            onChange={(e) => setNewSetDescription(e.target.value)}
            placeholder="Brief description of what this test set covers"
            rows={3}
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '0.875rem',
              resize: 'vertical',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <ActionButton onClick={() => setShowCreateSetModal(false)}>Cancel</ActionButton>
          <ActionButton onClick={handleCreateSet} variant="primary" loading={isCreating} disabled={!newSetName.trim()}>
            Create
          </ActionButton>
        </div>
      </div>
    </div>
  );

  // Run Test Modal
  const renderRunTestModal = () => showRunTestModal && (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }} onClick={() => setShowRunTestModal(false)}>
      <div style={{
        background: 'white',
        borderRadius: '4px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '500px',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 1rem', color: DARK_COLOR }}>Run Test</h3>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#64748b', marginBottom: '0.5rem' }}>Search Configuration</label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Results Limit</label>
              <input
                type="number"
                value={runConfig.search?.search_limit || 10}
                onChange={(e) => setRunConfig({
                  ...runConfig,
                  search: { ...runConfig.search, search_limit: parseInt(e.target.value) || 10 }
                })}
                min={1}
                max={50}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Vector Weight</label>
              <input
                type="number"
                value={runConfig.search?.vector_weight || 0.75}
                onChange={(e) => setRunConfig({
                  ...runConfig,
                  search: { ...runConfig.search, vector_weight: parseFloat(e.target.value) || 0.75 }
                })}
                min={0}
                max={1}
                step={0.05}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={runConfig.search?.use_semantic_ranker || false}
                onChange={(e) => setRunConfig({
                  ...runConfig,
                  search: { ...runConfig.search, use_semantic_ranker: e.target.checked }
                })}
              />
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Use Semantic Ranker</span>
            </label>
          </div>

          <div style={{ marginTop: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={runConfig.search?.include_reasoning !== false}
                onChange={(e) => setRunConfig({
                  ...runConfig,
                  search: { ...runConfig.search, include_reasoning: e.target.checked }
                })}
              />
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Include LLM Reasoning (uses more tokens)</span>
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <ActionButton onClick={() => setShowRunTestModal(false)}>Cancel</ActionButton>
          <ActionButton onClick={handleStartRun} variant="primary" loading={isCreating} icon={faPlay}>
            Start Test
          </ActionButton>
        </div>
      </div>
    </div>
  );

  // Generate Questions Modal
  const renderGenerateModal = () => showGenerateModal && (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }} onClick={() => setShowGenerateModal(false)}>
      <div style={{
        background: 'white',
        borderRadius: '4px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 1rem', color: DARK_COLOR }}>Generate Questions from Documents</h3>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#64748b', marginBottom: '0.5rem' }}>
            Questions per Page
          </label>
          <select
            value={questionsPerPage}
            onChange={(e) => setQuestionsPerPage(parseInt(e.target.value))}
            style={{
              padding: '0.5rem',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '0.875rem',
              background: 'white',
            }}
          >
            <option value={2}>2 questions</option>
            <option value={3}>3 questions</option>
            <option value={4}>4 questions</option>
            <option value={5}>5 questions</option>
          </select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#64748b', marginBottom: '0.5rem' }}>
            Select Source Documents ({selectedDocIds.length} selected)
          </label>
          <div style={{
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            maxHeight: '300px',
            overflow: 'auto',
          }}>
            {indexedDocs.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>
                No indexed documents available
              </div>
            ) : (
              indexedDocs.map(doc => (
                <label
                  key={doc.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    background: selectedDocIds.includes(doc.id) ? '#f0fdf4' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDocIds.includes(doc.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDocIds([...selectedDocIds, doc.id]);
                      } else {
                        setSelectedDocIds(selectedDocIds.filter(id => id !== doc.id));
                      }
                    }}
                  />
                  <div>
                    <div style={{ fontSize: '0.875rem', color: DARK_COLOR }}>{doc.filename}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{doc.chunk_count} chunks</div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <ActionButton onClick={() => setShowGenerateModal(false)}>Cancel</ActionButton>
          <ActionButton
            onClick={handleGenerateQuestions}
            variant="primary"
            loading={isCreating}
            disabled={selectedDocIds.length === 0}
          >
            Generate Questions
          </ActionButton>
        </div>
      </div>
    </div>
  );

  // Add Existing Questions Modal
  const renderAddQuestionModal = () => {
    if (!showAddQuestionModal) return null;

    const existingIds = new Set(selectedSet?.questions.map(q => q.id) || []);
    const availableQuestions = allQuestions.filter(q => !existingIds.has(q.id));

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }} onClick={() => setShowAddQuestionModal(false)}>
        <div style={{
          background: 'white',
          borderRadius: '4px',
          padding: '1.5rem',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'auto',
        }} onClick={e => e.stopPropagation()}>
          <h3 style={{ margin: '0 0 1rem', color: DARK_COLOR }}>Add Questions to Test Set</h3>

          {availableQuestions.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
              No available questions to add. Generate some questions first.
            </div>
          ) : (
            <div style={{
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              maxHeight: '400px',
              overflow: 'auto',
              marginBottom: '1rem',
            }}>
              {availableQuestions.map(q => (
                <label
                  key={q.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    background: selectedToAdd.includes(q.id) ? '#f0fdf4' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedToAdd.includes(q.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedToAdd([...selectedToAdd, q.id]);
                      } else {
                        setSelectedToAdd(selectedToAdd.filter(id => id !== q.id));
                      }
                    }}
                    style={{ marginTop: '0.25rem' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.875rem', color: DARK_COLOR, marginBottom: '0.25rem' }}>{q.query}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{q.expected_answer}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <ActionButton onClick={() => setShowAddQuestionModal(false)}>Cancel</ActionButton>
            <ActionButton
              onClick={() => handleAddQuestions(selectedToAdd)}
              variant="primary"
              disabled={selectedToAdd.length === 0}
            >
              Add {selectedToAdd.length} Question{selectedToAdd.length !== 1 ? 's' : ''}
            </ActionButton>
          </div>
        </div>
      </div>
    );
  };

  // Question Result Detail Modal
  const renderQuestionResultModal = () => selectedQuestionResult && (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }} onClick={() => setSelectedQuestionResult(null)}>
      <div style={{
        background: 'white',
        borderRadius: '4px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{
              padding: '0.25rem 0.75rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: selectedQuestionResult.found ? `${SUCCESS_COLOR}20` : `${ERROR_COLOR}20`,
              color: selectedQuestionResult.found ? SUCCESS_COLOR : ERROR_COLOR,
            }}>
              {selectedQuestionResult.found ? 'FOUND' : 'NOT FOUND'}
            </span>
            {selectedQuestionResult.confidence !== undefined && (
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                {(selectedQuestionResult.confidence * 100).toFixed(0)}% confidence
              </span>
            )}
          </div>
          <button
            onClick={() => setSelectedQuestionResult(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              padding: '0.25rem',
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Query */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '0.25rem' }}>Query</div>
          <div style={{ fontSize: '1rem', fontWeight: 500, color: DARK_COLOR }}>{selectedQuestionResult.query}</div>
        </div>

        {/* Expected Answer */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '0.25rem' }}>Expected Answer</div>
          <div style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.5, background: '#f8fafc', padding: '0.75rem', borderRadius: '4px' }}>
            {selectedQuestionResult.expected_answer}
          </div>
        </div>

        {/* Evaluation Reasoning */}
        {(selectedQuestionResult.evaluation_reasoning || selectedQuestionResult.reasoning) && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '0.25rem' }}>Evaluation Reasoning</div>
            <div style={{
              fontSize: '0.85rem',
              color: '#475569',
              lineHeight: 1.6,
              background: selectedQuestionResult.found ? '#f0fdf4' : '#fef2f2',
              padding: '0.75rem',
              borderRadius: '4px',
              borderLeft: `3px solid ${selectedQuestionResult.found ? SUCCESS_COLOR : ERROR_COLOR}`,
            }}>
              {selectedQuestionResult.evaluation_reasoning || selectedQuestionResult.reasoning}
            </div>
          </div>
        )}

        {/* Metrics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '1rem',
          marginBottom: '1.25rem',
          padding: '1rem',
          background: '#f8fafc',
          borderRadius: '4px',
        }}>
          <div>
            <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8' }}>Chunks Retrieved</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, color: DARK_COLOR }}>{selectedQuestionResult.chunks_retrieved}</div>
          </div>
          {selectedQuestionResult.chunks && selectedQuestionResult.chunks[0]?.score && (
            <div>
              <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8' }}>Top Score</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: DARK_COLOR }}>{selectedQuestionResult.chunks[0].score.toFixed(4)}</div>
            </div>
          )}
          {selectedQuestionResult.search_latency_ms && (
            <div>
              <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8' }}>Search Latency</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: DARK_COLOR }}>{selectedQuestionResult.search_latency_ms}ms</div>
            </div>
          )}
          {selectedQuestionResult.eval_latency_ms && (
            <div>
              <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8' }}>Eval Latency</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: DARK_COLOR }}>{selectedQuestionResult.eval_latency_ms}ms</div>
            </div>
          )}
          {selectedQuestionResult.first_relevant_rank && (
            <div>
              <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8' }}>First Relevant Rank</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: DARK_COLOR }}>#{selectedQuestionResult.first_relevant_rank}</div>
            </div>
          )}
        </div>

        {/* Retrieved Chunks */}
        {selectedQuestionResult.chunks && selectedQuestionResult.chunks.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '0.5rem' }}>
              Retrieved Chunks ({selectedQuestionResult.chunks.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {selectedQuestionResult.chunks.map((chunk, index) => (
                <div
                  key={index}
                  style={{
                    background: '#f8fafc',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    borderLeft: `3px solid ${index === 0 ? INFO_COLOR : '#e2e8f0'}`,
                  }}
                >
                  {/* Chunk header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: index === 0 ? INFO_COLOR : '#64748b',
                        background: index === 0 ? `${INFO_COLOR}15` : '#f1f5f9',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '4px',
                      }}>
                        #{index + 1}
                      </span>
                      {chunk.filename && (
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {chunk.filename}
                        </span>
                      )}
                      {chunk.page && (
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          Page {chunk.page}
                        </span>
                      )}
                    </div>
                    <span style={{
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: chunk.score > 0.8 ? SUCCESS_COLOR : chunk.score > 0.5 ? WARNING_COLOR : '#94a3b8',
                    }}>
                      Score: {chunk.score.toFixed(4)}
                    </span>
                  </div>
                  {/* Heading context */}
                  {chunk.heading && (
                    <div style={{ fontSize: '0.8rem', fontWeight: 500, color: DARK_COLOR, marginBottom: '0.25rem' }}>
                      {chunk.heading}
                    </div>
                  )}
                  {/* Summary if available */}
                  {chunk.summary && (
                    <div style={{ fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                      {chunk.summary}
                    </div>
                  )}
                  {/* Content */}
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#475569',
                    lineHeight: 1.5,
                    maxHeight: index === 0 ? '200px' : '100px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    background: 'white',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #e2e8f0',
                  }}>
                    {chunk.content || '(No content)'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legacy: Top Chunk Summary (for old data without full chunks) */}
        {!selectedQuestionResult.chunks && selectedQuestionResult.top_chunk_summary && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '0.25rem' }}>Top Chunk Summary</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5, fontStyle: 'italic', background: '#f8fafc', padding: '0.75rem', borderRadius: '4px' }}>
              {selectedQuestionResult.top_chunk_summary}
            </div>
          </div>
        )}

        {/* Close button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ActionButton onClick={() => setSelectedQuestionResult(null)}>Close</ActionButton>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div>
      {/* Sub-navigation */}
      {(subView === 'sets' || subView === 'runs' || subView === 'questions') && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          background: 'white',
          padding: '0.5rem',
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          {subTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSubView(tab.id)}
              style={{
                padding: '0.625rem 1rem',
                border: 'none',
                background: subView === tab.id ? ACCENT_COLOR : 'transparent',
                color: subView === tab.id ? 'white' : '#64748b',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s',
              }}
            >
              <FontAwesomeIcon icon={tab.icon} style={{ width: 14, height: 14 }} />
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {subView === 'sets' && renderSetsView()}
      {subView === 'set-detail' && renderSetDetailView()}
      {subView === 'runs' && renderRunsView()}
      {subView === 'run-detail' && renderRunDetailView()}
      {subView === 'questions' && renderQuestionsView()}

      {/* Modals */}
      {renderCreateSetModal()}
      {renderRunTestModal()}
      {renderGenerateModal()}
      {renderAddQuestionModal()}
      {renderQuestionResultModal()}
    </div>
  );
};

// ============================================================================
// Settings Tab
// ============================================================================

const SettingsTab: React.FC = () => {
  const [config, setConfig] = useState<RagConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const data = await apiService.getRagConfig();
        setConfig(data);
      } catch (error) {
        console.error('Failed to load config:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadConfig();
  }, []);

  // Helper to format config values
  const ConfigRow: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: '0.8rem', fontWeight: 500, color: DARK_COLOR }}>{value}</span>
    </div>
  );

  // Group file types by category
  const groupFileTypes = (types: string[]) => {
    const docs = types.filter(t => ['.pdf', '.docx', '.doc', '.pptx', '.ppt'].includes(t));
    const sheets = types.filter(t => ['.xlsx', '.xls'].includes(t));
    const web = types.filter(t => ['.html', '.htm'].includes(t));
    const images = types.filter(t => ['.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp', '.heif'].includes(t));
    return { docs, sheets, web, images };
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
        Loading configuration...
      </div>
    );
  }

  if (!config) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: ERROR_COLOR }}>
        Failed to load configuration
      </div>
    );
  }

  const fileTypeGroups = groupFileTypes(config.supported_file_types);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
      <SectionCard title="Chunking Configuration" icon={faCog}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <ConfigRow label="Max Chunk Tokens" value={config.chunking.max_chunk_tokens.toLocaleString()} />
          <ConfigRow label="Split Overlap Tokens" value={config.chunking.split_overlap_tokens.toLocaleString()} />
          <ConfigRow label="Max Table Rows per Chunk" value={config.chunking.max_table_rows} />
          <ConfigRow label="Table Row Overlap" value={config.chunking.table_row_overlap} />
        </div>
      </SectionCard>

      <SectionCard title="Summarization Settings" icon={faCog}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <ConfigRow label="Target Summary Tokens" value={config.summarization.summary_token_recommendation} />
          <ConfigRow label="Parallel Workers" value={config.summarization.parallel_workers} />
          <ConfigRow label="Max Retries" value={config.summarization.max_retries} />
          <ConfigRow label="Initial Backoff" value={`${config.summarization.initial_backoff_seconds}s`} />
          <ConfigRow label="Summary Model" value={config.summarization.model} />
        </div>
      </SectionCard>

      <SectionCard title="Search Defaults" icon={faCog}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <ConfigRow label="Default Vector Weight" value={config.search.default_vector_weight} />
          <ConfigRow label="Default Keyword Weight" value={config.search.default_keyword_weight} />
          <ConfigRow label="Embedding Model" value={config.search.embedding_model} />
          <ConfigRow label="Vector Dimensions" value={config.search.vector_dimensions.toLocaleString()} />
        </div>
      </SectionCard>

      <SectionCard title="Supported File Types" icon={faCog}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <ConfigRow label="Documents" value={fileTypeGroups.docs.join(' ')} />
          <ConfigRow label="Spreadsheets" value={fileTypeGroups.sheets.join(' ')} />
          <ConfigRow label="Web" value={fileTypeGroups.web.join(' ')} />
          <ConfigRow label="Images" value={fileTypeGroups.images.join(' ')} />
        </div>
      </SectionCard>
    </div>
  );
};

// ============================================================================
// Costs Tab
// ============================================================================

interface CostsTabProps {
  costs: RagCostsResponse | null;
  isLoading: boolean;
  onRefresh: () => void;
  periodDays: number;
  onPeriodChange: (days: number) => void;
}

const CostsTab: React.FC<CostsTabProps> = ({ costs, isLoading, onRefresh, periodDays, onPeriodChange }) => {
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
        Loading cost data...
      </div>
    );
  }

  if (!costs) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
        No cost data available
      </div>
    );
  }

  // Get ingestion costs (fallback to legacy fields for backwards compatibility)
  const ingestion = costs.ingestion || {
    total_cost_usd: costs.total_cost_usd,
    total_input_tokens: costs.total_input_tokens,
    total_output_tokens: costs.total_output_tokens,
    total_api_calls: costs.total_api_calls,
    costs_by_operation: costs.costs_by_operation,
    daily_costs: costs.daily_costs,
    top_documents: costs.top_documents,
    pricing_warnings: costs.pricing_warnings,
  };

  // Get testing costs (may not exist if no tests run)
  const testing = costs.testing;
  const hasTestCosts = testing && testing.total_cost_usd > 0;

  const summarizationCosts = ingestion.costs_by_operation['summarization'] || { total_cost_usd: 0, call_count: 0, total_input_tokens: 0, total_output_tokens: 0 };
  const embeddingCosts = ingestion.costs_by_operation['embedding'] || { total_cost_usd: 0, call_count: 0, total_input_tokens: 0, total_output_tokens: 0 };
  const testEvalCosts = testing?.costs_by_operation['evaluation'] || { total_cost_usd: 0, call_count: 0, total_input_tokens: 0, total_output_tokens: 0 };
  const testEmbeddingCosts = testing?.costs_by_operation['embedding'] || { total_cost_usd: 0, call_count: 0, total_input_tokens: 0, total_output_tokens: 0 };

  // Calculate projected monthly cost
  const dailyAverage = costs.total_cost_usd / periodDays;
  const projectedMonthly = dailyAverage * 30;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Scope Notice */}
      <div style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '4px',
        padding: '0.75rem 1rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
      }}>
        <FontAwesomeIcon icon={faDatabase} style={{ color: '#3b82f6', marginTop: '2px' }} />
        <div>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e40af' }}>RAG Pipeline LLM Costs</span>
          <p style={{ fontSize: '0.8rem', color: '#1e40af', margin: '0.25rem 0 0 0', lineHeight: 1.4 }}>
            Tracks LLM costs for <strong>document ingestion</strong> (summarization, embedding) and <strong>RAG test evaluation</strong> (evaluation, embeddings).
            Chat, reasoning, and Azure semantic reranking costs are not included.
          </p>
        </div>
      </div>

      {/* Period Selector and Refresh */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Period:</span>
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              onClick={() => onPeriodChange(days)}
              style={{
                padding: '0.4rem 0.8rem',
                borderRadius: '4px',
                border: 'none',
                fontSize: '0.8rem',
                fontWeight: 500,
                cursor: 'pointer',
                background: periodDays === days ? ACCENT_COLOR : '#e2e8f0',
                color: periodDays === days ? 'white' : DARK_COLOR,
                transition: 'all 0.2s',
              }}
            >
              {days} days
            </button>
          ))}
        </div>
        <ActionButton onClick={onRefresh} icon={faSync} variant="secondary">
          Refresh
        </ActionButton>
      </div>

      {/* Pricing Warning */}
      {costs.pricing_warnings > 0 && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '4px',
          padding: '0.75rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b' }} />
          <span style={{ fontSize: '0.85rem', color: '#92400e' }}>
            {costs.pricing_warnings} API calls have inaccurate pricing data. Costs may not reflect actual charges.
          </span>
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        <StatCard
          label="Total Cost"
          value={formatCurrency(costs.total_cost_usd)}
          subtitle={hasTestCosts ? `Ingestion: ${formatCurrency(ingestion.total_cost_usd)} | Testing: ${formatCurrency(testing?.total_cost_usd || 0)}` : `Last ${periodDays} days`}
          color={ACCENT_COLOR}
        />
        <StatCard
          label="Projected Monthly"
          value={formatCurrency(projectedMonthly)}
          subtitle="Based on current usage"
          color={INFO_COLOR}
        />
        <StatCard
          label="API Calls"
          value={costs.total_api_calls.toLocaleString()}
          subtitle={hasTestCosts ? `Ingestion: ${ingestion.total_api_calls.toLocaleString()} | Testing: ${testing?.total_api_calls.toLocaleString()}` : `${summarizationCosts.call_count.toLocaleString()} summaries, ${embeddingCosts.call_count.toLocaleString()} embeddings`}
          color={PURPLE_COLOR}
        />
        <StatCard
          label="Total Tokens"
          value={formatTokens(costs.total_input_tokens + costs.total_output_tokens)}
          subtitle={`${formatTokens(costs.total_input_tokens)} in, ${formatTokens(costs.total_output_tokens)} out`}
          color={SUCCESS_COLOR}
        />
      </div>

      {/* Cost by Operation */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
        <SectionCard title="Summarization Costs" icon={faDollarSign}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Cost</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: ACCENT_COLOR }}>{formatCurrency(summarizationCosts.total_cost_usd)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>API Calls</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{summarizationCosts.call_count.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Input Tokens</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{formatTokens(summarizationCosts.total_input_tokens)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Output Tokens</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{formatTokens(summarizationCosts.total_output_tokens)}</span>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Embedding Costs (text-embedding-3-small)" icon={faDollarSign}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Cost</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: ACCENT_COLOR }}>{formatCurrency(embeddingCosts.total_cost_usd)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>API Calls</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{embeddingCosts.call_count.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Tokens Processed</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{formatTokens(embeddingCosts.total_input_tokens)}</span>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Daily Cost Chart (simple bar visualization) */}
      {costs.daily_costs.length > 0 && (
        <SectionCard title="Daily Costs" icon={faChartBar}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {costs.daily_costs.slice(-14).map((day, idx) => {
              const maxCost = Math.max(...costs.daily_costs.map(d => d.total_cost_usd), 0.0001);
              const barWidth = (day.total_cost_usd / maxCost) * 100;
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', width: '60px', flexShrink: 0 }}>
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <div style={{ flex: 1, height: '20px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${Math.max(barWidth, 1)}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, ${ACCENT_COLOR}, ${INFO_COLOR})`,
                        borderRadius: '4px',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 500, color: DARK_COLOR, width: '70px', textAlign: 'right' }}>
                    {formatCurrency(day.total_cost_usd)}
                  </span>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Top Documents by Cost */}
      {costs.top_documents.length > 0 && (
        <SectionCard title="Top Documents by Cost" icon={faFileAlt}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {costs.top_documents.map((doc, idx) => (
              <div
                key={doc.document_id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem 0',
                  borderBottom: idx < costs.top_documents.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: '#64748b',
                  }}>
                    {idx + 1}
                  </span>
                  <span style={{ fontSize: '0.85rem', color: DARK_COLOR }}>{doc.filename}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    {formatTokens(doc.total_input_tokens + doc.total_output_tokens)} tokens
                  </span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: ACCENT_COLOR }}>
                    {formatCurrency(doc.total_cost_usd)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Testing Costs Section */}
      {hasTestCosts && (
        <>
          {/* Test Section Header */}
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            background: '#faf5ff',
            borderRadius: '4px',
            border: '1px solid #e9d5ff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faFlask} style={{ color: PURPLE_COLOR }} />
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#7c3aed' }}>RAG Test Evaluation Costs</span>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#6b21a8', margin: '0.25rem 0 0 0' }}>
              {testing?.total_runs || 0} test runs | {testing?.total_api_calls.toLocaleString()} API calls
            </p>
          </div>

          {/* Test Cost Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            <SectionCard title="Test Evaluation" icon={faDollarSign}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Cost</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: PURPLE_COLOR }}>{formatCurrency(testEvalCosts.total_cost_usd)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>API Calls</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{testEvalCosts.call_count.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Input Tokens</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{formatTokens(testEvalCosts.total_input_tokens)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Output Tokens</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{formatTokens(testEvalCosts.total_output_tokens)}</span>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Test Embeddings" icon={faDollarSign}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Cost</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: PURPLE_COLOR }}>{formatCurrency(testEmbeddingCosts.total_cost_usd)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>API Calls</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{testEmbeddingCosts.call_count.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Tokens Processed</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color: DARK_COLOR }}>{formatTokens(testEmbeddingCosts.total_input_tokens)}</span>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Top Test Runs by Cost */}
          {testing?.top_test_runs && testing.top_test_runs.length > 0 && (
            <SectionCard title="Top Test Runs by Cost" icon={faFlask}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {testing.top_test_runs.map((run, idx) => (
                  <div
                    key={run.test_run_id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0',
                      borderBottom: idx < testing.top_test_runs.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: '#faf5ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: PURPLE_COLOR,
                      }}>
                        {idx + 1}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: DARK_COLOR }}>{run.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {formatTokens(run.total_input_tokens + run.total_output_tokens)} tokens
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: PURPLE_COLOR }}>
                        {formatCurrency(run.total_cost_usd)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// Sync Preview Modal
// ============================================================================

interface SyncPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (maxCount?: number) => void;
  isLoading: boolean;
  isSyncing: boolean;
  previewData: RagSyncResult | null;
}

const SyncPreviewModal: React.FC<SyncPreviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  isSyncing,
  previewData,
}) => {
  const [maxCount, setMaxCount] = useState<string>('');

  if (!isOpen) return null;

  const hasChanges = previewData && (
    previewData.new_documents > 0 ||
    previewData.changed_documents > 0 ||
    (previewData.stale_processing || 0) > 0
  );

  const totalToProcess = previewData
    ? previewData.new_documents + previewData.changed_documents + (previewData.stale_processing || 0)
    : 0;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSyncing) onClose();
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '4px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: DARK_COLOR }}>
            Sync Documents
          </h3>
          {!isSyncing && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: '1.25rem',
                padding: '0.25rem',
              }}
            >
              &times;
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
              Scanning blob storage...
            </div>
          ) : isSyncing ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: ACCENT_COLOR }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem', fontSize: '1.5rem' }} />
              <div style={{ marginTop: '1rem', fontWeight: 500 }}>Syncing documents...</div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>
                This may take a few minutes
              </div>
            </div>
          ) : previewData ? (
            <div>
              <div style={{ marginBottom: '1.25rem', fontSize: '0.9rem', color: '#64748b' }}>
                Found {previewData.total_blobs} documents in blob storage:
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {previewData.new_documents > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f0fdf4', borderRadius: '4px', borderLeft: `3px solid ${SUCCESS_COLOR}` }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: SUCCESS_COLOR }} />
                    <span style={{ fontWeight: 500, color: '#166534' }}>{previewData.new_documents} new</span>
                    <span style={{ fontSize: '0.8rem', color: '#15803d' }}>will be indexed</span>
                  </div>
                )}

                {previewData.changed_documents > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#fefce8', borderRadius: '4px', borderLeft: `3px solid ${WARNING_COLOR}` }}>
                    <FontAwesomeIcon icon={faSync} style={{ color: WARNING_COLOR }} />
                    <span style={{ fontWeight: 500, color: '#854d0e' }}>{previewData.changed_documents} changed</span>
                    <span style={{ fontSize: '0.8rem', color: '#a16207' }}>will be re-indexed</span>
                  </div>
                )}

                {(previewData.stale_processing || 0) > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#fef2f2', borderRadius: '4px', borderLeft: `3px solid ${ERROR_COLOR}` }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: ERROR_COLOR }} />
                    <span style={{ fontWeight: 500, color: '#991b1b' }}>{previewData.stale_processing} stale</span>
                    <span style={{ fontSize: '0.8rem', color: '#b91c1c' }}>stuck processing &gt;6h, will retry</span>
                  </div>
                )}

                {(previewData.skipped_processing || 0) > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '4px', borderLeft: '3px solid #94a3b8' }}>
                    <FontAwesomeIcon icon={faClock} style={{ color: '#64748b' }} />
                    <span style={{ fontWeight: 500, color: '#475569' }}>{previewData.skipped_processing} processing</span>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>currently being processed, will skip</span>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f1f5f9', borderRadius: '4px', borderLeft: '3px solid #94a3b8' }}>
                  <FontAwesomeIcon icon={faDatabase} style={{ color: '#64748b' }} />
                  <span style={{ fontWeight: 500, color: '#475569' }}>{previewData.existing_documents} existing</span>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>already indexed, no changes needed</span>
                </div>
              </div>

              {previewData.documents_by_type && Object.keys(previewData.documents_by_type).length > 0 && (
                <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: 600 }}>
                    By File Type
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {Object.entries(previewData.documents_by_type).map(([type, count]) => (
                      <span
                        key={type}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: '#f1f5f9',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          color: '#475569',
                        }}
                      >
                        {type.toUpperCase()}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: ERROR_COLOR }}>
              Failed to load sync preview
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoading && !isSyncing && (
          <div
            style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.75rem',
            }}
          >
            {/* Max count input for testing */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Limit (for testing):
              </label>
              <input
                type="number"
                min="1"
                max={totalToProcess}
                value={maxCount}
                onChange={(e) => setMaxCount(e.target.value)}
                placeholder={`All (${totalToProcess})`}
                style={{
                  width: '100px',
                  padding: '0.375rem 0.5rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(maxCount ? parseInt(maxCount, 10) : undefined)}
                disabled={!hasChanges}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  background: hasChanges ? ACCENT_COLOR : '#e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: hasChanges ? 'pointer' : 'not-allowed',
                  color: hasChanges ? 'white' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FontAwesomeIcon icon={faSync} />
                {hasChanges
                  ? maxCount
                    ? `Sync ${maxCount} Documents`
                    : 'Start Sync'
                  : 'No Changes to Sync'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const RagAdminPage: React.FC = () => {
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = URL_TABS.find((t) => t === tabParam) ?? 'dashboard';
  const setActiveTab = (id: TabId) => {
    setSearchParams(id === 'dashboard' ? {} : { tab: id }, { replace: true });
  };
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [stats, setStats] = useState<RagStatsResponse | null>(null);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncPreviewLoading, setSyncPreviewLoading] = useState(false);
  const [syncPreviewData, setSyncPreviewData] = useState<RagSyncResult | null>(null);
  const [retryingDocumentId, setRetryingDocumentId] = useState<string | null>(null);
  const [retryStatus, setRetryStatus] = useState<string | null>(null);

  // Search state
  const [vectorWeight, setVectorWeight] = useState(0.75);
  const [useSemanticRanker, setUseSemanticRanker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RagSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Document viewer state
  const [selectedDocument, setSelectedDocument] = useState<RagDocument | null>(null);
  const [initialChunkId, setInitialChunkId] = useState<string | undefined>(undefined);
  const [initialPage, setInitialPage] = useState<number | undefined>(undefined);

  // Costs state
  const [costs, setCosts] = useState<RagCostsResponse | null>(null);
  const [isLoadingCosts, setIsLoadingCosts] = useState(false);
  const [costsPeriodDays, setCostsPeriodDays] = useState(30);

  // Handler for viewing a chunk in document viewer
  const handleViewChunk = useCallback((doc: RagDocument, chunkId: string, pageNumber?: number) => {
    setSelectedDocument(doc);
    setInitialChunkId(chunkId);
    setInitialPage(pageNumber);
  }, []);

  // Handler to clear viewer state
  const handleCloseViewer = useCallback(() => {
    setSelectedDocument(null);
    setInitialChunkId(undefined);
    setInitialPage(undefined);
  }, []);

  const loadDocuments = useCallback(async () => {
    setIsLoadingDocs(true);
    try {
      const response = await apiService.getRagDocuments();
      setDocuments(response.documents);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setIsLoadingDocs(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const response = await apiService.getRagStats();
      setStats(response);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  const loadCosts = useCallback(async (days: number = costsPeriodDays) => {
    setIsLoadingCosts(true);
    try {
      const response = await apiService.getRagCosts(days);
      setCosts(response);
    } catch (error) {
      console.error('Failed to load costs:', error);
    } finally {
      setIsLoadingCosts(false);
    }
  }, [costsPeriodDays]);

  useEffect(() => {
    loadDocuments();
    loadStats();
    loadCosts();
  }, [loadDocuments, loadStats, loadCosts]);

  const handleCostsPeriodChange = useCallback((days: number) => {
    setCostsPeriodDays(days);
    loadCosts(days);
  }, [loadCosts]);

  // Open sync modal and do dry-run
  const handleOpenSyncModal = async () => {
    setSyncModalOpen(true);
    setSyncPreviewLoading(true);
    setSyncPreviewData(null);
    setSyncMessage(null);
    try {
      const response = await apiService.triggerRagSync(true); // dry_run=true

      // Handle async job response (new API)
      if (response.job_id) {
        // Poll for dry_run results
        const pollPreview = async () => {
          try {
            const status = await apiService.getSyncJobStatus(response.job_id!);
            if (status.status === 'completed') {
              setSyncPreviewData(status.result || null);
              setSyncPreviewLoading(false);
            } else if (status.status === 'failed') {
              console.error('Sync preview failed:', status.error);
              setSyncPreviewLoading(false);
            } else {
              // Still pending/running - poll again
              setTimeout(pollPreview, 1000);
            }
          } catch (error) {
            console.error('Error polling preview status:', error);
            setSyncPreviewLoading(false);
          }
        };
        setTimeout(pollPreview, 500);
      } else {
        // Fallback for old API response format
        setSyncPreviewData(response.result || null);
        setSyncPreviewLoading(false);
      }
    } catch (error) {
      console.error('Failed to get sync preview:', error);
      setSyncPreviewLoading(false);
    }
  };

  // Close sync modal
  const handleCloseSyncModal = () => {
    setSyncModalOpen(false);
    setSyncPreviewData(null);
  };

  // Confirm and run actual sync
  const handleConfirmSync = async (maxCount?: number) => {
    setIsSyncing(true);
    handleCloseSyncModal();
    try {
      // Trigger sync - returns immediately with job_id
      const response = await apiService.triggerRagSync(false, maxCount);

      if (!response.job_id) {
        // Fallback for old API response format
        setSyncMessage(`Sync completed: ${response.result?.processed || 0} documents processed`);
        await loadDocuments();
        await loadStats();
        setIsSyncing(false);
        return;
      }

      setSyncMessage(`Sync job started (${response.job_id.slice(0, 8)}...) - processing in background...`);

      // Poll for job status
      const pollInterval = 3000; // 3 seconds
      const maxPolls = 600; // 30 minutes max
      let polls = 0;

      const pollStatus = async () => {
        try {
          const status = await apiService.getSyncJobStatus(response.job_id!);

          if (status.status === 'running') {
            const processed = status.result?.processed || 0;
            const total = status.result?.new_documents || 0;
            setSyncMessage(`Syncing... ${processed}/${total} documents processed`);
          }

          if (status.status === 'completed') {
            setSyncMessage(`Sync completed: ${status.result?.processed || 0} documents processed`);
            await loadDocuments();
            await loadStats();
            setIsSyncing(false);
            return;
          }

          if (status.status === 'failed') {
            setSyncMessage(`Sync failed: ${status.error || 'Unknown error'}`);
            setIsSyncing(false);
            return;
          }

          // Still pending or running - continue polling
          polls++;
          if (polls < maxPolls) {
            setTimeout(pollStatus, pollInterval);
          } else {
            setSyncMessage('Sync timed out - check logs for status');
            setIsSyncing(false);
          }
        } catch (error) {
          console.error('Error polling sync status:', error);
          setSyncMessage('Error checking sync status');
          setIsSyncing(false);
        }
      };

      // Start polling after a short delay
      setTimeout(pollStatus, 1000);

    } catch (error) {
      setSyncMessage(error instanceof Error ? error.message : 'Sync failed');
      setIsSyncing(false);
    }
  };

  const handleRetry = async (documentId: string) => {
    console.log('[RAG Admin] Retrying document:', documentId);
    setRetryingDocumentId(documentId);
    setRetryStatus('starting');

    try {
      const result = await apiService.retryRagDocument(documentId, (status, filename) => {
        console.log('[RAG Admin] Retry status:', status, filename);
        setRetryStatus(status);
      });

      console.log('[RAG Admin] Retry result:', result);

      if (!result.success) {
        console.error('[RAG Admin] Retry failed:', result.error);
        setRetryStatus(`failed: ${result.error}`);
        // Clear after 3 seconds
        setTimeout(() => {
          setRetryingDocumentId(null);
          setRetryStatus(null);
        }, 3000);
      } else {
        setRetryStatus('completed');
        await loadDocuments();
        // Clear after 1 second
        setTimeout(() => {
          setRetryingDocumentId(null);
          setRetryStatus(null);
        }, 1000);
      }
    } catch (error) {
      console.error('[RAG Admin] Failed to retry document:', error);
      setRetryStatus('error');
      setTimeout(() => {
        setRetryingDocumentId(null);
        setRetryStatus(null);
      }, 3000);
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document and all its chunks?')) return;
    try {
      await apiService.deleteRagDocument(documentId);
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      await loadStats();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const handleRetryAll = async () => {
    const failedDocs = documents.filter((d) => d.status === 'failed' || isStaleProcessing(d));
    for (const doc of failedDocs) {
      try {
        await apiService.retryRagDocument(doc.id);
      } catch (error) {
        console.error(`Failed to retry document ${doc.id}:`, error);
      }
    }
    await loadDocuments();
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      // keyword_weight is derived from vectorWeight (they sum to 1.0)
      const keywordWeight = 1 - vectorWeight;
      const response = await apiService.testRagSearch(searchQuery, vectorWeight, keywordWeight, 5, useSemanticRanker);
      setSearchResults(response.results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const tabs: { id: TabId; label: string; icon: typeof faDatabase; count?: number }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: faChartBar },
    { id: 'documents', label: 'Documents', icon: faFileAlt, count: documents.length },
    { id: 'chunks', label: 'Chunks', icon: faDatabase, count: stats?.total_chunks },
    { id: 'search', label: 'Search Lab', icon: faSliders },
    { id: 'costs', label: 'Costs', icon: faDollarSign },
    { id: 'errors', label: 'Errors', icon: faExclamationTriangle, count: documents.filter((d) => d.status === 'failed' || isStaleProcessing(d)).length },
    { id: 'tests', label: 'Tests', icon: faFlask },
    { id: 'feedback', label: 'Feedback Review', icon: faComment },
    { id: 'users', label: 'Users', icon: faUsers },
    { id: 'settings', label: 'Settings', icon: faCog },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Lato, system-ui, sans-serif' }}>
      {/* Header */}
      <header
        style={{
          background: 'rgba(10,10,10,0.97)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          padding: '0.875rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          gap: '1rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
          {/* Wolf icon + back navigation live in the shared Hub header (hubs/HubHeader.tsx). */}

          {/* Admin identity */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faDatabase} style={{ color: ACCENT_COLOR, width: 14, height: 14 }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0' }}>Lupa Administration</span>
          </div>
        </div>

        <ActionButton onClick={handleOpenSyncModal} icon={faSync} variant="primary" loading={isSyncing}>
          {isSyncing ? 'Syncing...' : 'Sync All Documents'}
        </ActionButton>
      </header>

      {/* Tabs */}
      <nav
        style={{
          background: 'white',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          padding: '0 2rem',
          gap: 0,
          overflowX: 'auto',
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.id === 'feedback') {
                // Navigate to dedicated Feedback Review page
                navigate('/feedback-review');
              } else {
                setActiveTab(tab.id);
              }
            }}
            style={{
              padding: '1rem 1.5rem',
              border: 'none',
              background: 'none',
              color: activeTab === tab.id ? ACCENT_COLOR : '#64748b',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              borderBottom: activeTab === tab.id ? `2px solid ${ACCENT_COLOR}` : '2px solid transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s',
              fontFamily: 'Lato, system-ui, sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            <FontAwesomeIcon icon={tab.icon} style={{ width: 14, height: 14 }} />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span
                style={{
                  background: activeTab === tab.id ? '#fef2f2' : '#f1f5f9',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '999px',
                  fontSize: '0.75rem',
                  color: activeTab === tab.id ? ACCENT_COLOR : '#64748b',
                }}
              >
                {tab.count.toLocaleString()}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Sync Message */}
      {syncMessage && (
        <div
          style={{
            margin: '1rem 2rem 0',
            padding: '0.75rem 1rem',
            borderRadius: '4px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            color: '#166534',
            fontSize: '0.875rem',
          }}
        >
          {syncMessage}
        </div>
      )}

      {/* Main Content */}
      <main style={{ padding: '1.5rem 2rem' }}>
        {activeTab === 'dashboard' && (
          <DashboardTab stats={stats} isLoading={isLoadingStats} onRefresh={loadStats} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab
            documents={documents}
            isLoading={isLoadingDocs}
            onRefresh={loadDocuments}
            onRetry={handleRetry}
            onDelete={handleDelete}
            onViewDocument={setSelectedDocument}
            retryingDocumentId={retryingDocumentId}
            retryStatus={retryStatus}
          />
        )}
        {activeTab === 'chunks' && <ChunksTab documents={documents} onViewChunk={handleViewChunk} />}
        {activeTab === 'search' && (
          <SearchLabTab
            vectorWeight={vectorWeight}
            onVectorWeightChange={setVectorWeight}
            useSemanticRanker={useSemanticRanker}
            onSemanticRankerChange={setUseSemanticRanker}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            searchResults={searchResults}
            isSearching={isSearching}
            onSearch={handleSearch}
            documents={documents}
            onViewInDocument={handleViewChunk}
          />
        )}
        {activeTab === 'costs' && (
          <CostsTab
            costs={costs}
            isLoading={isLoadingCosts}
            onRefresh={() => loadCosts(costsPeriodDays)}
            periodDays={costsPeriodDays}
            onPeriodChange={handleCostsPeriodChange}
          />
        )}
        {activeTab === 'errors' && (
          <ErrorsTab
            documents={documents}
            onRetry={handleRetry}
            onDelete={handleDelete}
            onRetryAll={handleRetryAll}
            retryingDocumentId={retryingDocumentId}
            retryStatus={retryStatus}
          />
        )}
        {activeTab === 'tests' && <TestsTab documents={documents} />}
        {activeTab === 'users' && <UsersTab currentUserId={currentUserId} />}
        {activeTab === 'settings' && <SettingsTab />}
      </main>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <DocumentViewerModal
          document={selectedDocument}
          onClose={handleCloseViewer}
          initialChunkId={initialChunkId}
          initialPage={initialPage}
        />
      )}

      {/* Sync Preview Modal */}
      <SyncPreviewModal
        isOpen={syncModalOpen}
        onClose={handleCloseSyncModal}
        onConfirm={handleConfirmSync}
        isLoading={syncPreviewLoading}
        isSyncing={isSyncing}
        previewData={syncPreviewData}
      />
    </div>
  );
};

export default RagAdminPage;
